/**
 * The photo half of the same union the Documents tab reads.
 *
 * Same endpoint, same dedupe, one query key — so a photo can never appear
 * in one tab and not the other, and opening both costs one round trip.
 * `kind` is decided server-side by `siteHistoryHelpers.itemKind` (mime
 * type first, category second), which is the same call the field app
 * makes; the office does not get its own opinion about what a photo is.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { AttachmentImage } from "@/components/attachment-image";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDate } from "@/lib/utils";
import { ImageIcon } from "lucide-react";
import { useSiteFiles, type SiteFileRow } from "./documents-section";
import type { SiteContext } from "./types";

export function PhotosSection({ site }: { site: SiteContext }) {
    const q = useSiteFiles(site.siteId);
    const [preview, setPreview] = useState<SiteFileRow | null>(null);

    const photos = (q.data?.groups ?? [])
        .flatMap((g) => g.items.map((i) => ({ ...i, deviceLabel: g.assetId ? (g.assetName ?? g.label) : null })))
        .filter((i) => i.kind === "photo")
        .sort((a, b) => (a.date === b.date ? (a.id < b.id ? 1 : -1) : a.date < b.date ? 1 : -1));

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                    <ImageIcon className="h-4 w-4" /> Photos ({photos.length})
                </CardTitle>
            </CardHeader>
            <CardContent>
                {q.isLoading ? (
                    <p className="text-sm text-foreground/70">Loading photos…</p>
                ) : photos.length === 0 ? (
                    <p className="text-sm text-foreground/70">
                        No photos at this location yet. Drop an image anywhere on this page to add one.
                    </p>
                ) : (
                    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                        {photos.map((p) => (
                            <li key={p.id}>
                                <button
                                    type="button"
                                    onClick={() => setPreview(p)}
                                    className="block w-full overflow-hidden rounded-md border border-border bg-card text-left hover:border-primary"
                                >
                                    {/* NOT a bare <img>. The download endpoint
                                        needs a JWT and an <img> cannot send an
                                        Authorization header, so the token has
                                        to ride in the URL — which is the only
                                        thing `AttachmentImage` does, and the
                                        reason it exists. A raw `src={thumbUrl}`
                                        401s into the browser's broken-image
                                        icon on every tile.

                                        The server's `thumbUrl`/`url` are bare
                                        paths by design: they are part of the
                                        shape the FIELD app shares, and it also
                                        renders them through this component. */}
                                    <div className="aspect-square w-full overflow-hidden">
                                        <AttachmentImage
                                            attachmentId={p.id}
                                            alt={p.photoLabel ?? p.name}
                                            loading="lazy"
                                            className="h-full w-full object-cover"
                                        />
                                    </div>
                                    <span
                                        className="line-clamp-2 block px-2 pt-1.5 text-xs text-card-foreground"
                                        title={p.photoLabel ?? p.name}
                                    >
                                        {p.photoLabel ?? p.name}
                                    </span>
                                    <span className="block px-2 pb-1.5 text-[11px] tabular-nums text-foreground/70">
                                        {formatDate(p.date)}
                                    </span>
                                </button>
                                {p.assetId && (
                                    <Link
                                        to={`/assets/${p.assetId}`}
                                        className="mt-1 block truncate text-[11px] text-primary hover:underline"
                                    >
                                        {p.deviceLabel ?? "Device"}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
            <AttachmentPreviewDialog
                open={!!preview}
                onClose={() => setPreview(null)}
                attachmentId={preview?.id ?? ""}
                fileName={preview?.photoLabel ?? preview?.name ?? "Photo"}
            />
        </Card>
    );
}
